/**
 * Wind Rose Module
 * Displays wind direction distribution as a radial histogram
 * Shows last 24 hours of wind data with speed color bands
 */

import { API_ENDPOINTS } from '../config.js';

// Speed range colors (from calm to strong)
const SPEED_COLORS = [
  '#81D4FA', // 1-2 mph - calm (light blue)
  '#66BB6A', // 2-5 mph - light (green)
  '#FFEE58', // 5-8 mph - moderate (yellow)
  '#FFA726', // 8-12 mph - strong (orange)
  '#AB47BC', // 12+ mph - very strong (purple)
];

// Refresh interval reference
let refreshInterval = null;

// Store current wind rose data
let windRoseData = null;

/**
 * Fetch wind rose data from API
 */
async function fetchWindRoseData() {
  try {
    const response = await fetch(API_ENDPOINTS.WIND_ROSE);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const data = await response.json();
    if (data.status === 'success') {
      // Data is at root level, not nested under .data
      return {
        bins: data.bins,
        totalObservations: data.totalObservations,
        speedRanges: data.speedRanges,
        speedUnit: data.speedUnit,
      };
    }
    return null;
  } catch (error) {
    console.error('[WindRose] Failed to fetch data:', error);
    return null;
  }
}

/**
 * Calculate SVG path for a wind rose petal (arc segment)
 * @param {number} centerX - Center X coordinate
 * @param {number} centerY - Center Y coordinate
 * @param {number} startAngle - Start angle in degrees
 * @param {number} endAngle - End angle in degrees
 * @param {number} innerRadius - Inner radius of the arc
 * @param {number} outerRadius - Outer radius of the arc
 * @returns {string} SVG path data
 */
function createArcPath(centerX, centerY, startAngle, endAngle, innerRadius, outerRadius) {
  // Convert angles to radians, adjusting so 0° is at top (North)
  const startRad = (startAngle - 90) * Math.PI / 180;
  const endRad = (endAngle - 90) * Math.PI / 180;

  const x1 = centerX + innerRadius * Math.cos(startRad);
  const y1 = centerY + innerRadius * Math.sin(startRad);
  const x2 = centerX + outerRadius * Math.cos(startRad);
  const y2 = centerY + outerRadius * Math.sin(startRad);
  const x3 = centerX + outerRadius * Math.cos(endRad);
  const y3 = centerY + outerRadius * Math.sin(endRad);
  const x4 = centerX + innerRadius * Math.cos(endRad);
  const y4 = centerY + innerRadius * Math.sin(endRad);

  const largeArc = endAngle - startAngle > 180 ? 1 : 0;

  return `M ${x1} ${y1} L ${x2} ${y2} A ${outerRadius} ${outerRadius} 0 ${largeArc} 1 ${x3} ${y3} L ${x4} ${y4} A ${innerRadius} ${innerRadius} 0 ${largeArc} 0 ${x1} ${y1} Z`;
}

/**
 * Render the wind rose SVG
 * @param {object} data - Wind rose data from API
 */
function renderWindRose(data) {
  const container = document.getElementById('wind-rose-svg');
  if (!container || !data || !data.bins) {
    return;
  }

  const centerX = 60;
  const centerY = 60;
  const maxRadius = 50;
  const innerRadius = 12;

  // Find max percentage to scale the petals
  const maxPercentage = Math.max(...data.bins.map(b => b.percentage), 1);

  let svgContent = '';

  // Draw concentric circles for reference
  const circles = [25, 50, 75, 100];
  circles.forEach(pct => {
    const r = innerRadius + (maxRadius - innerRadius) * (pct / 100);
    svgContent += `<circle cx="${centerX}" cy="${centerY}" r="${r}" class="wind-rose-grid-circle"/>`;
  });

  // Draw direction lines
  for (let i = 0; i < 16; i++) {
    const angle = (i * 22.5 - 90) * Math.PI / 180;
    const x2 = centerX + maxRadius * Math.cos(angle);
    const y2 = centerY + maxRadius * Math.sin(angle);
    svgContent += `<line x1="${centerX}" y1="${centerY}" x2="${x2}" y2="${y2}" class="wind-rose-grid-line"/>`;
  }

  // Draw petals for each direction
  data.bins.forEach((bin) => {
    if (bin.count === 0) {
      return;
    }

    const startAngle = bin.degrees - 11.25; // Half of 22.5° bin width
    const endAngle = bin.degrees + 11.25;

    // Draw stacked speed ranges from inside out
    let currentInnerRadius = innerRadius;

    bin.speeds.forEach((speedCount, speedIndex) => {
      if (speedCount === 0) {
        return;
      }

      // Calculate the height of this speed segment proportional to its count
      const segmentPercentage = (speedCount / data.totalObservations) * 100;
      const segmentHeight = (maxRadius - innerRadius) * (segmentPercentage / maxPercentage);
      const outerRadius = currentInnerRadius + segmentHeight;

      if (segmentHeight > 0.5) { // Only draw if visible
        const path = createArcPath(centerX, centerY, startAngle, endAngle, currentInnerRadius, outerRadius);
        svgContent += `<path d="${path}" fill="${SPEED_COLORS[speedIndex]}" class="wind-rose-petal" opacity="0.85"/>`;
      }

      currentInnerRadius = outerRadius;
    });
  });

  // Draw cardinal direction labels
  const labels = [
    { text: 'N', x: centerX, y: 8 },
    { text: 'E', x: 112, y: centerY + 4 },
    { text: 'S', x: centerX, y: 116 },
    { text: 'W', x: 8, y: centerY + 4 },
  ];

  labels.forEach(label => {
    const isNorth = label.text === 'N';
    svgContent += `<text x="${label.x}" y="${label.y}" class="wind-rose-label${isNorth ? ' wind-rose-label-n' : ''}">${label.text}</text>`;
  });

  // Draw center circle
  svgContent += `<circle cx="${centerX}" cy="${centerY}" r="${innerRadius}" class="wind-rose-center"/>`;

  container.innerHTML = svgContent;

  // Update observation count
  const countEl = document.getElementById('wind-rose-obs-count');
  if (countEl) {
    countEl.textContent = `${data.totalObservations} obs`;
  }
}

/**
 * Render the legend
 */
function renderLegend() {
  const container = document.getElementById('wind-rose-legend');
  if (!container) {
    return;
  }

  const speedRanges = ['1-2', '2-5', '5-8', '8-12', '12+'];
  let html = '';

  speedRanges.forEach((range, i) => {
    html += `<span class="wind-rose-legend-item">
      <span class="wind-rose-legend-color" style="background-color: ${SPEED_COLORS[i]}"></span>
      <span class="wind-rose-legend-label">${range}</span>
    </span>`;
  });

  container.innerHTML = html;
}

/**
 * Refresh wind rose data and re-render
 */
async function refreshWindRose() {
  const data = await fetchWindRoseData();

  if (data) {
    windRoseData = data;
    renderWindRose(data);
  }
}

/**
 * Initialize the wind rose
 */
export async function initializeWindRose() {
  // Render legend
  renderLegend();

  // Initial data fetch
  await refreshWindRose();

  // Set up refresh interval (every 5 minutes to match API cache)
  if (refreshInterval) {
    clearInterval(refreshInterval);
  }
  refreshInterval = setInterval(refreshWindRose, 5 * 60 * 1000);
}

/**
 * Stop the refresh interval
 */
export function stopWindRoseRefresh() {
  if (refreshInterval) {
    clearInterval(refreshInterval);
    refreshInterval = null;
  }
}

/**
 * Get current wind rose data
 */
export function getWindRoseData() {
  return windRoseData;
}
